// Changing the address you sign in with, from the account page (scenario A6,
// PLAN-2026-10-28-by-email §3.5, B-548).
//
// THE NEW ADDRESS AND THE CURRENT PASSWORD: a device left signed in is not
// enough to move the account to another inbox. And said before the press, in
// the line under the button: nothing changes until the link sent to the new
// address is used, and the old address hears about it with a way to undo it.

import { useState } from 'react';
import { useSession } from '../../data/session';
import { Button, Dialog, Line, PasswordField, Stack, Text, TextField, tell } from '../../kit/ui';

export function ChangeEmailSheet({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { session, changeEmail } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string; other?: string }>({});

  const close = () => {
    setEmail('');
    setPassword('');
    setErrors({});
    onClose();
  };

  const submit = () => {
    const to = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to))
      return setErrors({ email: 'That email does not look right yet.' });
    setBusy(true);
    setErrors({});
    void (async () => {
      const outcome = await changeEmail({ email: to, password });
      setBusy(false);
      if (outcome.ok) {
        close();
        tell(`A link is on its way to ${to}. Your email changes when you use it.`, 'good');
        return;
      }
      // A WRONG PASSWORD under the password; an address another account has,
      // or the one already in use, under the address; anything else beneath.
      if (outcome.code === 'forbidden') return setErrors({ password: outcome.message });
      if (outcome.code === 'conflict') return setErrors({ email: outcome.message });
      setErrors({ other: outcome.message });
    })();
  };

  const ready = email.trim().length > 0 && password.length > 0 && !busy;

  return (
    <Dialog opened={opened} onClose={close} title="Change your email">
      <Stack gap={3}>
        <TextField
          label="New email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(v) => {
            setEmail(v);
            setErrors((e) => ({ ...e, email: undefined }));
          }}
          error={errors.email}
          autoFocus
        />
        <PasswordField
          label="Current password"
          value={password}
          onChange={(v) => {
            setPassword(v);
            setErrors((e) => ({ ...e, password: undefined }));
          }}
          autoComplete="current-password"
          error={errors.password}
        />
        <Text tone="secondary" size="sm" block>
          We’ll email the new address a link. Nothing changes until you use it, and until then you
          sign in with {session?.email ?? 'your email'}. Once it changes, that address gets a note
          with a way to undo it for 24 hours.
        </Text>
        {errors.other ? (
          <Text tone="bad" block>
            {errors.other}
          </Text>
        ) : null}
        <Line end>
          <Button variant="quiet" onClick={close}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!ready} loading={busy} onClick={submit}>
            Send the link
          </Button>
        </Line>
      </Stack>
    </Dialog>
  );
}
